import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { EstimateTotal } from '../models/estimates/estimate-total';

@Injectable({
  providedIn: 'root'
})
export class EstimateTotalService {
  // BehaviorSubjects for each total type
  private laborTotalSubject = new BehaviorSubject<number>(0);
  private laborTaxPercentageSubject = new BehaviorSubject<number>(0);
  private laborTaxTotalSubject = new BehaviorSubject<number>(0);

  private partTotalSubject = new BehaviorSubject<number>(0);
  private partTaxPercentageSubject = new BehaviorSubject<number>(0);
  private partTaxTotalSubject = new BehaviorSubject<number>(0);

  private supplyTotalSubject = new BehaviorSubject<number>(0);
  private supplyTaxPercentageSubject = new BehaviorSubject<number>(0);
  private supplyTaxTotalSubject = new BehaviorSubject<number>(0);

  // Observables for components to subscribe to
  public readonly laborTotal$ = this.laborTotalSubject.asObservable();
  public readonly laborTaxPercentage$ = this.laborTaxPercentageSubject.asObservable();
  public readonly laborTaxTotal$ = this.laborTaxTotalSubject.asObservable();

  public readonly partTotal$ = this.partTotalSubject.asObservable();
  public readonly partTaxPercentage$ = this.partTaxPercentageSubject.asObservable();
  public readonly partTaxTotal$ = this.partTaxTotalSubject.asObservable();

  public readonly supplyTotal$ = this.supplyTotalSubject.asObservable();
  public readonly supplyTaxPercentage$ = this.supplyTaxPercentageSubject.asObservable();
  public readonly supplyTaxTotal$ = this.supplyTaxTotalSubject.asObservable();

  // Combined observable that creates EstimateTotal when any value changes
  public readonly estimateTotal$: Observable<EstimateTotal> = combineLatest([
    this.laborTotal$,
    this.laborTaxPercentage$,
    this.laborTaxTotal$,
    this.partTotal$,
    this.partTaxPercentage$,
    this.partTaxTotal$,
    this.supplyTotal$,
    this.supplyTaxPercentage$,
    this.supplyTaxTotal$
  ]).pipe(
    map(([laborTotal, laborTaxPercentage, laborTaxTotal, partTotal, partTaxPercentage, partTaxTotal, supplyTotal, supplyTaxPercentage, supplyTaxTotal]) =>
      new EstimateTotal(
        laborTotal,
        laborTaxPercentage,
        laborTaxTotal,
        partTotal,
        partTaxPercentage,
        partTaxTotal,
        supplyTotal,
        supplyTaxPercentage,
        supplyTaxTotal
      )
    )
  );

  /**
   * Update all totals at once - preserves existing calculation logic
   * @param laborTotal - calculated labor total
   * @param laborTaxPercentage - labor tax percentage
   * @param laborTaxTotal - calculated labor tax total
   * @param partTotal - calculated part total
   * @param partTaxPercentage - part tax percentage
   * @param partTaxTotal - calculated part tax total
   * @param supplyTotal - calculated supply total
   * @param supplyTaxPercentage - supply tax percentage
   * @param supplyTaxTotal - calculated supply tax total
   */
  public updateTotals(
    laborTotal: number,
    laborTaxPercentage: number,
    laborTaxTotal: number,
    partTotal: number,
    partTaxPercentage: number,
    partTaxTotal: number,
    supplyTotal: number,
    supplyTaxPercentage: number,
    supplyTaxTotal: number
  ): void {
    this.laborTotalSubject.next(laborTotal);
    this.laborTaxPercentageSubject.next(laborTaxPercentage);
    this.laborTaxTotalSubject.next(laborTaxTotal);

    this.partTotalSubject.next(partTotal);
    this.partTaxPercentageSubject.next(partTaxPercentage);
    this.partTaxTotalSubject.next(partTaxTotal);

    this.supplyTotalSubject.next(supplyTotal);
    this.supplyTaxPercentageSubject.next(supplyTaxPercentage);
    this.supplyTaxTotalSubject.next(supplyTaxTotal);
  }

  /**
   * Reset all totals to zero
   */
  public resetTotals(): void {
    this.updateTotals(0, 0, 0, 0, 0, 0, 0, 0, 0);
  }

  /**
   * Get current estimate total synchronously
   */
  public getCurrentEstimateTotal(): EstimateTotal {
    return new EstimateTotal(
      this.laborTotalSubject.value,
      this.laborTaxPercentageSubject.value,
      this.laborTaxTotalSubject.value,
      this.partTotalSubject.value,
      this.partTaxPercentageSubject.value,
      this.partTaxTotalSubject.value,
      this.supplyTotalSubject.value,
      this.supplyTaxPercentageSubject.value,
      this.supplyTaxTotalSubject.value
    );
  }

  /**
   * Global method for triggering recalculation from anywhere in the application
   * This allows individual estimate components to trigger updates when their values change
   */
  private static _globalRecalculationTrigger?: () => void;

  public static setGlobalRecalculationTrigger(trigger: () => void): void {
    EstimateTotalService._globalRecalculationTrigger = trigger;
  }

  public static triggerGlobalRecalculation(): void {
    if (EstimateTotalService._globalRecalculationTrigger) {
      EstimateTotalService._globalRecalculationTrigger();
    }
  }
}